/**
 * O player de item.
 *
 * Um único `switch (interaction.kind)` decide a interação; `motion` e `audio` são
 * flags ortogonais de apresentação. É o que o esquema comprou: acrescentar um nível
 * de interatividade não é acrescentar um ramo aqui.
 *
 * Três modos. `practice` e `exam` recebem uma resposta; `review` MOSTRA uma resposta
 * já dada — a alternativa marcada, o número digitado, a construção feita —, com a
 * correção à vista e sem aceitar mudanças. É o que o aluno usa para rever o percurso,
 * e o que um professor usará para ver, a partir da telemetria, o que cada aluno fez.
 */

import { formatBR, lerNumeroBR } from '../core/num.ts';
import { conta } from '../core/pt.ts';
import { nunca } from '../core/nunca.ts';
import { evaluate } from '../geom/construction.ts';
import { grade } from '../engine/grade.ts';
import type { ToolAction, ToolId } from '../geom/tools.ts';
import { replay } from '../geom/tools.ts';
import { GRADE_TOL } from '../geom/tolerance.ts';
import { animate, prefersReducedMotion, type Animator } from '../render/animate.ts';
import { constructionToScene } from '../render/geom-scene.ts';
import { refletirY, scene as criarCena, type Scene } from '../render/scene.ts';
import { lerViewBox, renderScene } from '../render/svg.ts';
import { variantFeatureVector } from '../schema/difficulty.ts';
import type { ConstructInteraction, ItemInstance, Stimulus } from '../schema/item.ts';
import type { Vec2 } from '../geom/vec.ts';
import { criarArrastador } from './arrastador.ts';
import { criarOrdenador, type Ordenador } from './ordenador.ts';
import { criarBalanca } from './balanca.ts';
import { criarSeletorRegioes, type SeletorRegioes } from './regioes.ts';
import { criarRegua } from './regua.ts';
import { ErroExpressao, lerExpressao, mostrar } from '../core/expressao.ts';
import type { GradingResult, Response } from '../schema/response.ts';
import type { RichText } from '../schema/richtext.ts';
import type { ChoiceId } from '../schema/taxonomy.ts';
import type { PlayerMode, TelemetryCollector } from '../schema/telemetry.ts';
import { renderComentario } from './comentario.ts';
import { createConstructionPad, type ConstructionPad } from './constructionPad.ts';
import { renderRichInline, renderRichText } from './richtext.ts';

export interface PlayerOptions {
  readonly mode: PlayerMode;
  readonly telemetry?: TelemetryCollector;
  readonly onGraded?: (result: GradingResult, response: Response) => void;
  /** Mostrar o comentário detalhado depois de responder (laboratório). */
  readonly showRationale?: boolean;
  /**
   * Mostrar os metadados de autoria — nível, task-model, habilidade, b̂ e a proveniência
   * do b̂. Padrão: **não**. Eles pertencem à bancada de quem escreve item, e não à tela
   * de quem responde; antes eram sempre construídos e escondidos por CSS, o que voltava
   * a aparecer em qualquer contêiner novo.
   */
  readonly showMeta?: boolean;
  /** Modo `review`: a resposta já dada e a correção dela. */
  readonly review?: { readonly response: Response; readonly result: GradingResult };
}

export interface ItemPlayer {
  readonly element: HTMLElement;
  /** Envia a resposta atual para correção — o mesmo que clicar em "Responder". */
  responder(): void;
  dispose(): void;
}

export function createPlayer(instance: ItemInstance, opts: PlayerOptions): ItemPlayer {
  // Regra de sala de aula imposta pelo tipo, não pela memória de quem monta a prova.
  const exigeAudio = instance.stimuli.some((s) => s.audio.need === 'required');
  if (exigeAudio && opts.mode === 'exam') {
    throw new Error(
      `item ${instance.templateId} exige áudio e não pode ser aplicado em modo exame`,
    );
  }

  const revisao = opts.mode === 'review' ? opts.review : undefined;
  // Revisão não é tentativa: não abre registro de telemetria nem conta tempo.
  const telemetria = revisao ? undefined : opts.telemetry;

  const root = document.createElement('article');
  root.className = revisao ? 'item is-revisao' : 'item';

  const descartaveis: Array<() => void> = [];
  let respondido = revisao !== undefined;
  const inicio = performance.now();

  const item = {
    templateId: instance.templateId,
    templateVersion: instance.templateVersion,
    seed: instance.seed,
    instanceHash: instance.instanceHash,
  };

  telemetria?.startAttempt(`${instance.instanceHash}-${Date.now()}`);
  telemetria?.emit(
    {
      type: 'item.presented',
      level: instance.level,
      skills: instance.skills,
      provisionalB: instance.difficulty.provisionalB,
      variantFeatures: variantFeatureVector(instance.difficulty),
      mode: opts.mode,
      viewport: { w: window.innerWidth, h: window.innerHeight },
      reducedMotion: prefersReducedMotion(),
    },
    item,
  );

  // --- cabeçalho de autoria (só onde foi pedido) ----------------------------
  if (opts.showMeta) {
    const meta = document.createElement('header');
    meta.className = 'item-meta';
    meta.append(
      chip(instance.level, 'nivel'),
      chip(instance.taskModel as string, 'tm'),
      chip(instance.skills.join(', '), 'hab'),
      chip(`b̂ ${formatBR(instance.difficulty.provisionalB, 2)}`, 'b'),
      chip(instance.difficulty.status, 'status'),
    );
    root.appendChild(meta);
  }

  // --- enunciado ------------------------------------------------------------
  root.appendChild(renderRichText(instance.stem, 'div'));

  // --- estímulos ------------------------------------------------------------
  //
  // Num item de construção a PRANCHETA é o estímulo: ela desenha os mesmos dados, com
  // as mesmas convenções, e ainda deixa mexer. Mostrar a figura estática logo acima
  // dela duplicava a imagem e empurrava a área de trabalho para fora da primeira tela
  // — o aluno rolava meia tela de conteúdo redundante antes de poder desenhar. A
  // descrição textual da figura não se perde: vai para a `desc` da prancheta.
  const construindo = instance.interaction.kind === 'construct';
  const figuraSuprimida = construindo
    ? instance.stimuli.find((s) => s.kind === 'static-figure')
    : undefined;
  const estimulos = construindo
    ? instance.stimuli.filter((s) => s.kind !== 'static-figure')
    : instance.stimuli;

  // Os controles dos estímulos manipuláveis ficam acessíveis à interação: num item em
  // que a resposta É o valor de um seletor da figura, é daqui que ela é lida.
  const controles = new Map<string, ControleManipulavel>();
  for (const s of estimulos) {
    const r = renderStimulus(s, instance, telemetria);
    root.appendChild(r.element);
    if (r.dispose) descartaveis.push(r.dispose);
    if (r.controle) controles.set(s.id, r.controle);
  }

  // --- comando --------------------------------------------------------------
  //
  // A pergunta fecha o item, DEPOIS do suporte. Um item se lê em três tempos —
  // contexto, figura, pergunta — e inverter os dois últimos obriga o aluno a ler o
  // comando, olhar a figura, esquecer o comando e voltar a lê-lo.
  if (instance.comando.length > 0) {
    const cmd = renderRichText(instance.comando, 'div');
    cmd.classList.add('item-comando');
    root.appendChild(cmd);
  }

  // --- interação ------------------------------------------------------------
  const area = document.createElement('div');
  area.className = 'item-response';
  root.appendChild(area);

  const rodape = document.createElement('div');
  rodape.className = 'item-actions';
  const enviar = document.createElement('button');
  enviar.type = 'button';
  enviar.className = 'btn btn-primary';
  enviar.textContent = 'Responder';
  rodape.appendChild(enviar);
  if (revisao) rodape.hidden = true;
  root.appendChild(rodape);

  // --- dicas graduadas ------------------------------------------------------
  //
  // Uma por clique, da mais geral à mais específica. No exame, nenhuma; na revisão,
  // voltam as que o aluno pediu, para ele ver com que ajuda respondeu.
  const dicas = instance.dicas ?? [];
  let dicasUsadas = revisao ? Math.min(revisao.result.evidence.dicasUsadas ?? 0, dicas.length) : 0;
  const listaDicas = document.createElement('ol');
  listaDicas.className = 'dicas';
  listaDicas.setAttribute('aria-live', 'polite');
  const mostrarDica = (i: number): void => {
    const d = dicas[i];
    if (!d) return;
    const li = document.createElement('li');
    li.className = 'dica';
    const rot = document.createElement('span');
    rot.className = 'dica-rotulo';
    rot.textContent = `Dica ${i + 1}`;
    li.append(rot, renderRichInline(d));
    listaDicas.appendChild(li);
  };
  for (let i = 0; i < dicasUsadas; i++) mostrarDica(i);
  listaDicas.hidden = dicasUsadas === 0;
  root.insertBefore(listaDicas, rodape);
  let pedirDica: HTMLButtonElement | null = null;
  if (dicas.length > 0 && opts.mode !== 'exam' && !revisao) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-dica';
    const rotular = (): void => {
      b.textContent =
        dicasUsadas >= dicas.length
          ? 'Sem mais dicas'
          : `Pedir uma dica (${dicasUsadas + 1} de ${dicas.length})`;
      b.disabled = dicasUsadas >= dicas.length;
    };
    b.addEventListener('click', () => {
      if (respondido || dicasUsadas >= dicas.length) return;
      mostrarDica(dicasUsadas);
      telemetria?.emit({ type: 'hint.revealed', index: dicasUsadas }, item);
      dicasUsadas++;
      listaDicas.hidden = false;
      rotular();
    });
    rotular();
    rodape.insertBefore(b, enviar);
    pedirDica = b;
  }

  const retorno = document.createElement('div');
  retorno.className = 'item-feedback';
  retorno.setAttribute('aria-live', 'polite');
  root.appendChild(retorno);

  let obterResposta: () => Response = () => ({ kind: 'omitted' });
  /**
   * Trava a interação depois do envio. Uma resposta corrigida não pode continuar
   * mudando na tela: o aluno veria uma coisa, a correção teria julgado outra — e a
   * revisão, se lesse o estado da tela, mostraria uma resposta que nunca foi enviada.
   */
  let travar: () => void = () => {};
  let ordenador: Ordenador | null = null;
  let seletor: SeletorRegioes | null = null;

  switch (instance.interaction.kind) {
    case 'select-one': {
      let escolhida: ChoiceId | null =
        revisao?.response.kind === 'select-one' ? revisao.response.choiceId : null;
      const lista = document.createElement('div');
      // Alternativas que são figuras (E6) vão em grade: empilhados, cinco gráficos de
      // largura inteira passavam de dois mil pixels de altura.
      const comFiguras = instance.interaction.alternatives.some((a) => a.figure);
      lista.className = comFiguras ? 'choices com-figuras' : 'choices';
      lista.setAttribute('role', 'radiogroup');

      for (const alt of instance.interaction.alternatives) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'choice';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', String(alt.id === escolhida));
        b.dataset['id'] = alt.id;
        if (revisao) b.disabled = true;

        const marca = document.createElement('span');
        marca.className = 'choice-id';
        marca.textContent = alt.id;
        const corpo = document.createElement('span');
        corpo.className = 'choice-body';
        corpo.appendChild(renderRichInline(alt.content));
        if (alt.figure) corpo.appendChild(renderScene(alt.figure));
        b.append(marca, corpo);

        b.addEventListener('click', () => {
          if (respondido) return;
          const anterior = escolhida;
          escolhida = alt.id;
          for (const el of lista.querySelectorAll('.choice')) {
            el.setAttribute('aria-checked', String((el as HTMLElement).dataset['id'] === alt.id));
          }
          telemetria?.emit({ type: 'choice.selected', choiceId: alt.id, previous: anterior }, item);
        });
        lista.appendChild(b);
      }

      area.appendChild(lista);
      obterResposta = () =>
        escolhida === null ? { kind: 'omitted' } : { kind: 'select-one', choiceId: escolhida };
      travar = () => {
        for (const el of lista.querySelectorAll<HTMLButtonElement>('.choice')) el.disabled = true;
      };
      break;
    }

    case 'numeric-entry': {
      // RESPOSTA PELO SELETOR DA FIGURA. Não há campo para digitar: o aluno move o
      // controle, vê a figura mudar e envia o valor em que parou. Enquanto ele não
      // mexer, não há resposta — o valor inicial do seletor nunca conta como escolha.
      const via = instance.interaction.viaControl;
      if (via) {
        const controle = controles.get(via.stimulusId);
        const aviso = document.createElement('p');
        aviso.className = 'numeric-via';
        aviso.textContent = 'Sua resposta é o valor escolhido no seletor da figura.';
        area.appendChild(aviso);

        if (revisao) {
          if (revisao.response.kind === 'numeric-entry' && revisao.response.value !== null) {
            controle?.definir(via.controlId, revisao.response.value);
          }
          controle?.travar();
        }
        obterResposta = () => {
          const v = controle?.valor(via.controlId) ?? null;
          return v === null
            ? { kind: 'omitted' }
            : { kind: 'numeric-entry', raw: formatBR(v), value: v };
        };
        travar = () => controle?.travar();
        break;
      }

      const wrap = document.createElement('div');
      wrap.className = 'numeric';
      const input = document.createElement('input');
      input.type = 'text';
      input.inputMode = 'decimal';
      input.className = 'numeric-input';
      input.setAttribute('aria-label', 'Sua resposta');
      if (revisao) {
        input.value = revisao.response.kind === 'numeric-entry' ? revisao.response.raw : '';
        input.disabled = true;
      }
      wrap.appendChild(input);
      if (instance.interaction.unit) {
        const u = document.createElement('span');
        u.className = 'numeric-unit';
        u.textContent = instance.interaction.unit;
        wrap.appendChild(u);
      }
      input.addEventListener('input', () => {
        telemetria?.emit({ type: 'numeric.changed', raw: input.value }, item);
      });
      area.appendChild(wrap);
      // Capturada aqui: dentro do fechamento o tipo da interação deixa de estar estreitado.
      const unidade = instance.interaction.unit;
      obterResposta = () => ({
        kind: 'numeric-entry',
        raw: input.value,
        value: lerNumeroBR(input.value, unidade),
      });
      travar = () => {
        input.disabled = true;
      };
      break;
    }

    case 'coordinate-entry': {
      const inter = instance.interaction;
      const rev = revisao?.response.kind === 'coordinate-entry' ? revisao.response : null;
      const ficha = document.createElement('div');
      ficha.className = 'coord-ficha';
      ficha.appendChild(renderScene(inter.ficha));
      const vb = inter.ficha.viewBox;
      const campos: [HTMLInputElement, HTMLInputElement][] = [];

      for (const p of inter.pontos) {
        const rot = document.createElement('div');
        rot.className = `coord-rotulo lado-${p.lado}`;
        // O SVG inverte o eixo y; a posição do rótulo segue a mesma conversão.
        rot.style.left = `${((p.em.x - vb.x) / vb.w) * 100}%`;
        rot.style.top = `${((refletirY(vb, p.em.y) - vb.y) / vb.h) * 100}%`;
        const nome = document.createElement('span');
        nome.className = 'coord-nome';
        nome.textContent = p.nome;
        rot.appendChild(nome);
        if (p.valor) {
          rot.classList.add('is-dado');
          rot.appendChild(
            document.createTextNode(`(${formatBR(p.valor.x)}, ${formatBR(p.valor.y)})`),
          );
        } else {
          const k = campos.length;
          const x = campoCoord(`${p.nome}: abscissa (x)`);
          const y = campoCoord(`${p.nome}: ordenada (y)`);
          if (rev) {
            x.value = rev.raw[k]?.[0] ?? '';
            y.value = rev.raw[k]?.[1] ?? '';
            x.disabled = true;
            y.disabled = true;
          }
          for (const c of [x, y]) {
            c.addEventListener('input', () => {
              telemetria?.emit(
                { type: 'numeric.changed', raw: `${p.nome} = (${x.value}; ${y.value})` },
                item,
              );
            });
          }
          const par = document.createElement('span');
          par.className = 'coord-par';
          par.append('(', x, ',', y, ')');
          rot.appendChild(par);
          campos.push([x, y]);
        }
        ficha.appendChild(rot);
      }
      area.appendChild(ficha);

      obterResposta = () => {
        const raw = campos.map(([x, y]) => [x.value, y.value] as const);
        if (raw.every(([a, b]) => a.trim() === '' && b.trim() === '')) return { kind: 'omitted' };
        const valores = raw.map(([a, b]): Vec2 | null => {
          const vx = lerNumeroBR(a);
          const vy = lerNumeroBR(b);
          return vx === null || vy === null ? null : { x: vx, y: vy };
        });
        return { kind: 'coordinate-entry', raw, valores };
      };
      travar = () => {
        for (const [x, y] of campos) {
          x.disabled = true;
          y.disabled = true;
        }
      };
      break;
    }

    case 'region-select': {
      const inter = instance.interaction;
      const inicial =
        revisao?.response.kind === 'region-select' ? revisao.response.selecionadas : [];
      const s = criarSeletorRegioes(inter, inicial, revisao !== undefined);
      seletor = s;
      area.appendChild(s.element);
      obterResposta = () =>
        s.tocado()
          ? { kind: 'region-select', selecionadas: s.selecionadas() }
          : { kind: 'omitted' };
      travar = () => s.travar();
      break;
    }

    case 'balance-ops': {
      // A RESPOSTA É A TRAJETÓRIA. O componente guarda as ações; o estado final vai
      // junto só como cache, e o corretor reexecuta tudo.
      const inter = instance.interaction;
      const b = criarBalanca(
        inter,
        revisao !== undefined,
        revisao?.response.kind === 'balance-ops' ? revisao.response.acoes : [],
      );
      area.appendChild(b.element);
      obterResposta = () =>
        b.tocado()
          ? { kind: 'balance-ops', acoes: b.acoes(), estado: b.estado() }
          : { kind: 'omitted' };
      travar = () => b.travar();
      break;
    }

    case 'control-fit': {
      // RESPOSTA PELOS CONTROLES DA FIGURA, todos juntos. Enquanto o aluno não mexer
      // em nenhum, não há resposta; depois, conta a posição de todos — inclusive a de
      // um controle que ele deixou onde estava.
      const inter = instance.interaction;
      const controle = controles.get(inter.stimulusId);
      const aviso = document.createElement('p');
      aviso.className = 'numeric-via';
      aviso.textContent = 'Sua resposta é a posição em que você deixar os controles da figura.';
      area.appendChild(aviso);
      if (revisao) {
        if (revisao.response.kind === 'control-fit') {
          for (const id of inter.controles) {
            const v = revisao.response.valores[id];
            if (v !== undefined) controle?.definir(id, v);
          }
        }
        controle?.travar();
      }
      obterResposta = () => {
        if (!controle || !inter.controles.some((id) => controle.valor(id) !== null))
          return { kind: 'omitted' };
        const valores: Record<string, number> = {};
        for (const id of inter.controles) valores[id] = controle.atual(id);
        return { kind: 'control-fit', valores, movimentos: controle.movimentos() };
      };
      travar = () => controle?.travar();
      break;
    }

    case 'expression-entry': {
      const inter = instance.interaction;
      const wrap = document.createElement('div');
      wrap.className = 'expr';
      const linha = document.createElement('label');
      linha.className = 'expr-linha';
      const prefixo = document.createElement('span');
      prefixo.className = 'expr-prefixo';
      prefixo.textContent = inter.prefixo;
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'expr-input';
      input.autocomplete = 'off';
      input.spellcheck = false;
      input.setAttribute('aria-label', `Sua expressão: ${inter.prefixo}`);
      linha.append(prefixo, input);
      const previa = document.createElement('p');
      previa.className = 'expr-previa';
      previa.setAttribute('aria-live', 'polite');
      const dica = document.createElement('p');
      dica.className = 'expr-dica';
      dica.textContent = `Use ${inter.variavel} para a variável; por exemplo, ${inter.exemplo}. A multiplicação pode ficar implícita: 3${inter.variavel} é 3 × ${inter.variavel}.`;
      // A prévia diz como o texto foi LIDO — nunca se está certo.
      const atualizarPrevia = (): void => {
        const t = input.value;
        if (t.trim() === '') {
          previa.textContent = '';
          previa.className = 'expr-previa';
          return;
        }
        try {
          previa.textContent = `Lida como: ${inter.prefixo} ${mostrar(lerExpressao(t, inter.variavel), inter.variavel)}`;
          previa.className = 'expr-previa is-lida';
        } catch (err) {
          previa.textContent =
            err instanceof ErroExpressao ? err.message : 'Ainda não é uma expressão completa.';
          previa.className = 'expr-previa is-incompleta';
        }
      };
      input.addEventListener('input', () => {
        atualizarPrevia();
        telemetria?.emit({ type: 'numeric.changed', raw: input.value }, item);
      });
      if (revisao) {
        input.value = revisao.response.kind === 'expression-entry' ? revisao.response.raw : '';
        input.disabled = true;
        atualizarPrevia();
      }
      wrap.append(linha, previa, dica);
      area.appendChild(wrap);
      obterResposta = () =>
        input.value.trim() === ''
          ? { kind: 'omitted' }
          : { kind: 'expression-entry', raw: input.value };
      travar = () => {
        input.disabled = true;
      };
      break;
    }

    case 'order': {
      const inter = instance.interaction;
      const rev = revisao?.response.kind === 'order' ? revisao.response : null;
      const ord: Ordenador = criarOrdenador(
        inter.itens,
        rev ? rev.ordem : inter.itens.map((_x, i) => i),
        inter.pontas,
        rev !== null,
      );
      ordenador = ord;
      area.appendChild(ord.element);
      // Sem mexer na lista não há resposta: a ordem de partida é do sorteio, não do aluno.
      obterResposta = () =>
        ord.tocado() ? { kind: 'order', ordem: ord.ordem() } : { kind: 'omitted' };
      travar = () => ord.travar();
      break;
    }

    case 'error-step': {
      const inter = instance.interaction;
      let escolhido: number | null =
        revisao?.response.kind === 'error-step' ? revisao.response.passo : null;
      const lista = document.createElement('ol');
      lista.className = 'passos';
      lista.setAttribute('role', 'radiogroup');
      lista.setAttribute('aria-label', 'Passos da resolução');
      inter.passos.forEach((passo, i) => {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'passo';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', String(i === escolhido));
        b.dataset['i'] = String(i);
        if (revisao) b.disabled = true;
        const num = document.createElement('span');
        num.className = 'passo-num';
        num.textContent = `Passo ${i + 1}`;
        const corpo = document.createElement('span');
        corpo.className = 'passo-corpo';
        corpo.appendChild(renderRichInline(passo));
        b.append(num, corpo);
        b.addEventListener('click', () => {
          if (respondido) return;
          escolhido = i;
          for (const el of lista.querySelectorAll<HTMLElement>('.passo')) {
            el.setAttribute('aria-checked', String(el.dataset['i'] === String(i)));
          }
          telemetria?.emit({ type: 'numeric.changed', raw: `passo ${i + 1}` }, item);
        });
        li.appendChild(b);
        lista.appendChild(li);
      });
      area.appendChild(lista);
      obterResposta = () =>
        escolhido === null ? { kind: 'omitted' } : { kind: 'error-step', passo: escolhido };
      travar = () => {
        for (const el of lista.querySelectorAll<HTMLButtonElement>('.passo')) el.disabled = true;
      };
      break;
    }

    case 'drag-points': {
      const inter = instance.interaction;
      const rev = revisao?.response.kind === 'drag-points' ? revisao.response : null;
      let marcas: readonly Scene['shapes'][number][] = [];
      if (rev) {
        try {
          marcas = inter.alvo.reviewShapes?.(rev.posicoes) ?? [];
        } catch {
          marcas = [];
        }
      }
      const arr = criarArrastador(inter, rev ? rev.posicoes : inter.pontos.map((p) => p.inicial), {
        bloqueado: rev !== null,
        extras: marcas,
        aoMover: (i, p) => {
          telemetria?.emit(
            {
              type: 'stimulus.interacted',
              stimulusId: 'arrastar',
              control: inter.pontos[i]?.nome ?? String(i),
              value: Math.round(p.x * 1000 + p.y),
            },
            item,
          );
        },
      });
      area.appendChild(arr.element);
      if (rev && inter.alvo.reviewLegend) area.appendChild(legenda(inter.alvo.reviewLegend));
      descartaveis.push(() => arr.dispose());
      obterResposta = () => ({
        kind: 'drag-points',
        posicoes: arr.posicoes(),
        movimentos: arr.movimentos(),
      });
      travar = () => arr.travar();
      break;
    }

    case 'construct': {
      const inter = instance.interaction;

      // Na revisão, a prancheta dá lugar a uma FIGURA da construção do aluno, com as
      // marcas de correção por cima. Reabrir a prancheta convidaria a mexer numa
      // resposta que já foi entregue.
      if (revisao) {
        area.appendChild(figuraDeRevisao(inter, revisao.response, figuraSuprimida?.alt));
        break;
      }

      const givenIds = new Set(inter.givens.order.map((g) => g as string));

      // O objetivo NÃO é repetido aqui: ele já está no comando, logo acima da
      // prancheta. `target.describe` continua existindo — é o que o corretor usa para
      // explicar o que era esperado, e o que o leitor de tela anuncia.
      const pad: ConstructionPad = createConstructionPad({
        givens: inter.givens,
        toolbelt: inter.toolbelt,
        snapping: inter.snapping,
        givenIds,
        ...(inter.backdrop ? { backdrop: inter.backdrop } : {}),
        ...(inter.viewBox ? { viewBox: inter.viewBox } : {}),
        ...(figuraSuprimida ? { describe: figuraSuprimida.alt } : {}),
        ...(inter.labelCreatedPoints === false ? { rotularPontos: false } : {}),
        ...(inter.ligarPontos ? { ligarPontos: inter.ligarPontos } : {}),
        onAction: (a: ToolAction) => {
          telemetria?.emit({ type: 'tool.applied', action: a }, item);
          if (a.tool === 'undo') {
            telemetria?.emit({ type: 'construction.undo', toSeq: a.toSeq }, item);
          }
        },
        onToolSelected: (t: ToolId) => {
          telemetria?.emit({ type: 'tool.selected', tool: t }, item);
        },
      });

      area.appendChild(pad.element);
      descartaveis.push(() => pad.dispose());

      obterResposta = () => ({
        kind: 'construct',
        // Cópia congelada no instante do envio.
        trajectory: [...pad.trajectory()],
        snapshot: pad.snapshot(),
      });
      travar = () => pad.bloquear();
      break;
    }
    default:
      nunca(instance.interaction, 'player: interação');
  }

  // --- correção -------------------------------------------------------------
  function responder(): void {
    if (respondido) return;
    respondido = true;
    enviar.disabled = true;

    const resposta = obterResposta();
    travar();
    if (pedirDica) pedirDica.disabled = true;
    telemetria?.emit({ type: 'response.submitted', response: resposta }, item);

    const resultado = grade(instance, resposta, {
      latencyMs: Math.round(performance.now() - inicio),
      attemptIndex: 0,
      ...(dicasUsadas > 0 ? { dicasUsadas } : {}),
    });

    telemetria?.emit(
      {
        type: 'item.graded',
        correct: resultado.correct,
        score: resultado.score,
        evidence: resultado.evidence,
        provisionalB: instance.difficulty.provisionalB,
        envelope: [instance.difficulty.lo, instance.difficulty.hi],
        variantFeatures: variantFeatureVector(instance.difficulty),
      },
      item,
    );

    mostrarResultado(resultado, resposta);
    opts.onGraded?.(resultado, resposta);
  }

  enviar.addEventListener('click', responder);

  function mostrarResultado(r: GradingResult, resposta: Response): void {
    retorno.replaceChildren();
    retorno.className = `item-feedback ${r.correct ? 'is-ok' : r.score > 0 ? 'is-partial' : 'is-err'}`;

    const cabeca = document.createElement('p');
    cabeca.className = 'feedback-head';
    cabeca.textContent = r.correct
      ? 'Correto'
      : r.score > 0
        ? `Parcialmente correto — ${Math.round(r.score * 100)}%`
        : 'Incorreto';
    retorno.appendChild(cabeca);
    retorno.appendChild(renderRichText(r.feedback));

    if (r.evidence.dicasUsadas) {
      const p = document.createElement('p');
      p.className = 'dicas-usadas';
      p.textContent = `Você usou ${conta(r.evidence.dicasUsadas, 'dica')}.`;
      retorno.appendChild(p);
    }

    // Construção correta num item com meta: as estrelas da economia.
    if (r.estrelas) {
      const e = r.estrelas;
      const p = document.createElement('p');
      p.className = 'estrelas';
      const icones = document.createElement('span');
      icones.className = 'estrelas-icones';
      icones.setAttribute('aria-hidden', 'true');
      icones.textContent = '★'.repeat(e.obtidas) + '☆'.repeat(3 - e.obtidas);
      const texto = document.createElement('span');
      texto.textContent =
        e.obtidas === 3
          ? ` ${conta(e.obtidas, 'estrela')}: ${conta(e.movimentos, 'movimento')}, dentro da meta de ${e.meta}.`
          : ` ${conta(e.obtidas, 'estrela')}: ${conta(e.movimentos, 'movimento')}. ${instance.interaction.kind === 'construct' ? 'A construção mais curta que conhecemos' : 'O caminho mais curto'} usa ${conta(e.meta, 'movimento')}.`;
      p.append(icones, texto);
      retorno.appendChild(p);
    }

    // Em múltipla escolha, revelar a chave depois de responder.
    if (instance.interaction.kind === 'select-one') {
      const keyId = instance.interaction.keyId;
      for (const el of root.querySelectorAll('.choice')) {
        const id = (el as HTMLElement).dataset['id'];
        if (id === keyId) el.classList.add('is-key');
        else if (el.getAttribute('aria-checked') === 'true') el.classList.add('is-wrong');
      }
    }

    // Resposta digitada: a caixa ganha a cor do veredito.
    if (instance.interaction.kind === 'numeric-entry') {
      root.querySelector('.numeric-input')?.classList.add(r.correct ? 'is-ok' : 'is-err');
    }
    if (instance.interaction.kind === 'coordinate-entry') {
      root.querySelector('.coord-ficha')?.classList.add(r.correct ? 'is-ok' : 'is-err');
    }
    if (instance.interaction.kind === 'expression-entry') {
      root.querySelector('.expr-input')?.classList.add(r.correct ? 'is-ok' : 'is-err');
    }
    if (instance.interaction.kind === 'region-select') seletor?.marcar(instance.interaction.chave);
    if (instance.interaction.kind === 'order' && resposta.kind === 'order')
      ordenador?.marcar(instance.interaction.ordem);
    // Encontre o erro: o primeiro passo errado se revela, como a chave na escolha.
    if (instance.interaction.kind === 'error-step') {
      const certo = String(instance.interaction.passoErrado);
      for (const el of root.querySelectorAll<HTMLElement>('.passo')) {
        if (el.dataset['i'] === certo) el.classList.add('is-key');
        else if (el.getAttribute('aria-checked') === 'true') el.classList.add('is-wrong');
      }
    }

    if (r.evidence.rubric.length > 0) {
      const ul = document.createElement('ul');
      ul.className = 'rubric';
      for (const c of r.evidence.rubric) {
        const li = document.createElement('li');
        li.className = c.earned > 0 ? 'is-ok' : 'is-miss';
        li.textContent = `${c.earned > 0 ? '✓' : '·'} ${c.criterion}`;
        ul.appendChild(li);
      }
      retorno.appendChild(ul);
    }

    if (opts.showRationale !== false) {
      const det = document.createElement('details');
      det.className = 'rationale';
      const sum = document.createElement('summary');
      sum.textContent = 'Comentário detalhado';
      det.append(sum, renderComentario(instance, r, resposta));
      retorno.appendChild(det);
    }
  }

  if (revisao) mostrarResultado(revisao.result, revisao.response);

  return {
    element: root,
    responder,
    dispose() {
      for (const d of descartaveis) d();
    },
  };
}

// ---------------------------------------------------------------------------
// Revisão de construção
// ---------------------------------------------------------------------------

function campoCoord(rotulo: string): HTMLInputElement {
  const c = document.createElement('input');
  c.type = 'text';
  c.className = 'coord-campo';
  c.autocomplete = 'off';
  c.setAttribute('aria-label', rotulo);
  return c;
}

/**
 * A construção do aluno como FIGURA, com as marcas de correção do item por cima.
 *
 * A trajetória é reexecutada, como na correção: o que se mostra é o que foi corrigido,
 * não um retrato guardado que poderia divergir dele.
 */
function figuraDeRevisao(
  inter: ConstructInteraction,
  resposta: Response,
  descricao: string | undefined,
): HTMLElement {
  const fig = document.createElement('figure');
  fig.className = 'stimulus revisao-figura';

  const snap =
    resposta.kind === 'construct' ? replay(inter.givens, resposta.trajectory) : inter.givens;
  const avaliado = evaluate(snap);
  const givenIds = new Set(inter.givens.order.map((g) => g as string));

  const base = constructionToScene(avaliado, {
    title: 'Sua construção',
    desc: descricao ?? 'A construção entregue, com as marcas da correção.',
    givenIds,
    snapshot: snap,
    ...(inter.viewBox ? { viewBox: inter.viewBox } : {}),
  });

  let marcas: readonly Scene['shapes'][number][] = [];
  try {
    marcas =
      inter.target.reviewShapes?.(avaliado.objects, evaluate(inter.givens).objects, GRADE_TOL) ??
      [];
  } catch {
    marcas = [];
  }

  const cena = criarCena([...(inter.backdrop ?? []), ...base.shapes, ...marcas], {
    a11y: base.a11y,
    viewBox: inter.viewBox ?? base.viewBox,
  });
  fig.appendChild(renderScene(cena));
  if (inter.target.reviewLegend) fig.appendChild(legenda(inter.target.reviewLegend));
  return fig;
}

// ---------------------------------------------------------------------------
// Estímulos
// ---------------------------------------------------------------------------

/** Acesso aos controles de um estímulo manipulável. */
interface ControleManipulavel {
  /** Valor atual — ou `null` se o aluno nunca mexeu nesse controle. */
  valor(id: string): number | null;
  /** Valor atual, tenha o aluno mexido ou não: para respostas de vários controles. */
  atual(id: string): number;
  /** Quantas vezes um controle foi solto numa posição nova. */
  movimentos(): number;
  definir(id: string, v: number): void;
  travar(): void;
}

function renderStimulus(
  s: Stimulus,
  instance: ItemInstance,
  telemetria: TelemetryCollector | undefined,
): { element: HTMLElement; dispose?: () => void; controle?: ControleManipulavel } {
  const fig = document.createElement('figure');
  fig.className = 'stimulus';

  const item = {
    templateId: instance.templateId,
    templateVersion: instance.templateVersion,
    seed: instance.seed,
    instanceHash: instance.instanceHash,
  };

  if (s.kind === 'table') {
    const tabela = document.createElement('table');
    const thead = document.createElement('thead');
    const trh = document.createElement('tr');
    for (const hcell of s.headers) {
      const th = document.createElement('th');
      th.appendChild(renderRichInline(hcell));
      trh.appendChild(th);
    }
    thead.appendChild(trh);
    const tbody = document.createElement('tbody');
    for (const linha of s.rows) {
      const tr = document.createElement('tr');
      for (const cel of linha) {
        const td = document.createElement('td');
        td.appendChild(renderRichInline(cel));
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    tabela.append(thead, tbody);
    fig.appendChild(tabela);
    if (s.caption) fig.appendChild(legenda(s.caption));
    return { element: fig };
  }

  if (s.kind === 'static-figure') {
    fig.appendChild(renderScene(s.scene));
    if (s.caption) fig.appendChild(legenda(s.caption));
    return { element: fig };
  }

  if (s.kind === 'measurable') {
    // Figura parada, régua por cima: a régua é ferramenta, e nada nela vira resposta.
    const palco = document.createElement('div');
    palco.className = 'stage medir';
    const svg = renderScene(s.scene);
    palco.appendChild(svg);
    const regua = criarRegua(svg, s.scene.viewBox, s.regua, (angulo) => {
      telemetria?.emit(
        {
          type: 'stimulus.interacted',
          stimulusId: s.id,
          control: 'regua',
          value: Math.round(angulo),
        },
        item,
      );
    });
    fig.append(palco);
    if (s.caption) fig.appendChild(legenda(s.caption));
    return { element: fig, dispose: () => regua.dispose() };
  }

  if (s.kind === 'manipulable') {
    const palco = document.createElement('div');
    palco.className = 'stage';
    const valores: Record<string, number> = {};
    for (const c of s.controls) valores[c.id] = c.initial;

    const redesenhar = (): void => {
      palco.replaceChildren(renderScene(s.frame(valores)));
    };

    const controles = document.createElement('div');
    controles.className = 'controls';
    const entradas = new Map<
      string,
      {
        readonly input: HTMLInputElement;
        readonly saida: HTMLOutputElement;
        readonly format: (v: number) => string;
      }
    >();
    const tocados = new Set<string>();
    let mudancas = 0;
    for (const c of s.controls) {
      const linha = document.createElement('label');
      linha.className = 'control';
      const nome = document.createElement('span');
      nome.appendChild(renderRichInline(c.label));
      const input = document.createElement('input');
      input.type = 'range';
      input.min = String(c.min);
      input.max = String(c.max);
      input.step = String(c.step);
      input.value = String(c.initial);
      const saida = document.createElement('output');
      saida.textContent = c.format(c.initial);

      entradas.set(c.id, { input, saida, format: c.format });

      let debounce = 0;
      input.addEventListener('input', () => {
        const v = Number(input.value);
        tocados.add(c.id);
        valores[c.id] = v;
        saida.textContent = c.format(v);
        redesenhar();
        window.clearTimeout(debounce);
        debounce = window.setTimeout(() => {
          telemetria?.emit(
            { type: 'stimulus.interacted', stimulusId: s.id, control: c.id, value: v },
            item,
          );
        }, 120);
      });

      input.addEventListener('change', () => {
        mudancas++;
      });

      linha.append(nome, input, saida);
      controles.appendChild(linha);
    }

    fig.append(palco, controles);
    if (s.caption) fig.appendChild(legenda(s.caption));
    redesenhar();
    return {
      element: fig,
      controle: {
        valor: (id) => (tocados.has(id) ? (valores[id] ?? null) : null),
        atual: (id) => valores[id] ?? 0,
        movimentos: () => mudancas,
        definir: (id, v) => {
          const e = entradas.get(id);
          if (!e) return;
          valores[id] = v;
          e.input.value = String(v);
          e.saida.textContent = e.format(v);
          tocados.add(id);
          redesenhar();
        },
        travar: () => {
          for (const e of entradas.values()) e.input.disabled = true;
        },
      },
    };
  }

  // animado
  const palco = document.createElement('div');
  palco.className = 'stage';
  fig.appendChild(palco);

  // A BARRA DE TEMPO FICA EM LINHA PRÓPRIA, rotulada — e, quando a cena declara o
  // trecho que ela deve cobrir (`timeline`), com o comprimento EXATO desse trecho. No
  // item de percurso isso põe a régua do tempo logo abaixo da estrada, do mesmo
  // tamanho, e a comparação que o item quer provocar fica visível: o móvel já andou
  // metade do caminho, mas o tempo ainda não chegou à metade.
  const barra = document.createElement('div');
  barra.className = s.timeline ? 'playbar tem-trilho' : 'playbar';

  const trilho = document.createElement('label');
  trilho.className = 'playbar-trilho';
  const rotuloTempo = document.createElement('span');
  rotuloTempo.className = 'playbar-rotulo';
  rotuloTempo.textContent = 'Tempo';

  const posicao = document.createElement('input');
  posicao.type = 'range';
  posicao.min = '0';
  posicao.max = '1000';
  posicao.value = '0';
  posicao.className = 'scrub';
  posicao.setAttribute('aria-label', 'Tempo decorrido na animação');
  trilho.append(rotuloTempo, posicao);

  const controles = document.createElement('div');
  controles.className = 'playbar-controles';
  const botao = document.createElement('button');
  botao.type = 'button';
  botao.className = 'btn btn-icon';
  const replayBtn = document.createElement('button');
  replayBtn.type = 'button';
  replayBtn.className = 'btn btn-icon';
  replayBtn.textContent = '↺';
  replayBtn.title = 'Recomeçar';
  controles.append(botao, replayBtn);

  barra.append(trilho, controles);
  fig.appendChild(barra);

  const animador: Animator = animate(palco, s.frame, {
    durationMs: s.durationMs,
    loop: s.loop,
    // Sob `prefers-reduced-motion`, nada de autoplay: mostra-se o quadro estático.
    autoplay: !prefersReducedMotion(),
    onTick: (t) => {
      posicao.value = String(Math.round(t * 1000));
      posicao.style.setProperty('--p', String(t));
    },
    onPlayState: (p) => {
      botao.textContent = p ? '❙❙' : '▶';
      botao.title = p ? 'Pausar' : 'Reproduzir';
    },
  });

  if (prefersReducedMotion()) animador.seek(s.staticFallbackAt);
  botao.textContent = animador.playing ? '❙❙' : '▶';

  botao.addEventListener('click', () => {
    animador.toggle();
    telemetria?.emit(
      {
        type: 'stimulus.playback',
        stimulusId: s.id,
        action: animador.playing ? 'play' : 'pause',
        atMs: Math.round(animador.t * s.durationMs),
      },
      item,
    );
  });

  replayBtn.addEventListener('click', () => {
    animador.seek(0);
    animador.play();
    telemetria?.emit(
      { type: 'stimulus.playback', stimulusId: s.id, action: 'replay', atMs: 0 },
      item,
    );
  });

  posicao.addEventListener('input', () => {
    animador.pause();
    animador.seek(Number(posicao.value) / 1000);
  });

  // Alinhamento medido, não suposto: a figura pode ficar mais estreita que o quadro
  // (altura máxima, tela de celular), e só a medida diz onde a estrada caiu.
  const tl = s.timeline;
  let observador: ResizeObserver | null = null;
  if (tl) {
    const alinhar = (): void => {
      const svg = palco.querySelector('svg');
      const vb = svg ? lerViewBox(svg) : null;
      if (!svg || !vb) return;
      const rs = svg.getBoundingClientRect();
      const rb = barra.getBoundingClientRect();
      if (rs.width < 1 || rs.height < 1 || rb.width < 1) return;
      // `preserveAspectRatio="xMidYMid meet"`: vale a menor escala, conteúdo centrado.
      const k = Math.min(rs.width / vb.w, rs.height / vb.h);
      const inicioConteudo = rs.left + (rs.width - vb.w * k) / 2;
      const x0 = inicioConteudo + (tl.x0 - vb.x) * k - rb.left;
      const x1 = inicioConteudo + (tl.x1 - vb.x) * k - rb.left;
      barra.style.setProperty('--trilho-x', `${x0.toFixed(1)}px`);
      barra.style.setProperty('--trilho-w', `${(x1 - x0).toFixed(1)}px`);
    };
    if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(alinhar);
    if (typeof ResizeObserver !== 'undefined') {
      observador = new ResizeObserver(alinhar);
      observador.observe(fig);
    }
  }

  if (s.caption) fig.appendChild(legenda(s.caption));
  return {
    element: fig,
    dispose: () => {
      animador.dispose();
      observador?.disconnect();
    },
  };
}

function legenda(r: RichText): HTMLElement {
  const cap = document.createElement('figcaption');
  cap.appendChild(renderRichInline(r));
  return cap;
}

function chip(texto: string, tipo: string): HTMLElement {
  const el = document.createElement('span');
  el.className = `chip chip-${tipo}`;
  el.textContent = texto;
  return el;
}
